import { API_ENDPOINTS } from '../../config/apiEndpoints';
import { User, PaginatedResponse, UserFilters, BulkOperationLog } from '../../types';
import { UserStats } from '../../types/api.types';
import apiClient from '../../utils/apiClient';
import { updateUserPreferenceSchema } from '../../validations/userPreference.schema';

/**
 * Service for user management API calls.
 * Adheres to strict typing and project mandates.
 */
export const userApi = {
    /**
     * Get the authenticated user's profile.
     */
    getProfile: async (): Promise<User> => {
        return apiClient<User>(API_ENDPOINTS.USERS.ME.PROFILE, {
            method: 'GET',
        });
    },

    /**
     * Update the authenticated user's profile.
     */
    updateProfile: async (payload: Partial<User>): Promise<{ user: User; message: string }> => {
        return apiClient<{ user: User; message: string }>(API_ENDPOINTS.USERS.ME.PROFILE, {
            method: 'PUT',
            data: payload,
        });
    },

    /**
     * Fetch users with filtering and pagination.
     */
    fetchUsers: async (params: Partial<UserFilters> & { page: number; pageSize: number; sortKey?: string; sortDirection?: string }): Promise<PaginatedResponse<User>> => {
        return apiClient<PaginatedResponse<User>>(API_ENDPOINTS.USERS.BASE, {
            method: 'GET',
            params: params as unknown as Record<string, string>,
        });
    },

    /**
     * Fetch high-level user statistics.
     */
    fetchUserStats: async (): Promise<UserStats> => {
        return apiClient<UserStats>(API_ENDPOINTS.USERS.STATS, {
            method: 'GET',
        });
    },

    /**
     * Delete a user by ID.
     */
    deleteUser: async (id: string): Promise<void> => {
        return apiClient<void>(API_ENDPOINTS.USERS.BY_ID(id), {
            method: 'DELETE',
        });
    },

    /**
     * Update user status.
     */
    changeUserStatus: async (id: string, status: string): Promise<void> => {
        return apiClient<void>(API_ENDPOINTS.USERS.STATUS(id), {
            method: 'PATCH',
            data: { status },
        });
    },

    /**
     * Trigger a password reset for a user.
     */
    resetUserPassword: async (id: string): Promise<void> => {
        return apiClient<void>(API_ENDPOINTS.USERS.RESET_PASSWORD(id), {
            method: 'POST',
        });
    },

    /**
     * Resend welcome credentials (including secure note link) to a user.
     * @security Highly sensitive operation, triggers credential rotation and delivery.
     */
    resendWelcomeCredentials: async (
        id: string,
        payload: {
            deliveryMethod?: 'EMAIL' | 'SMS' | 'SECURE_NOTE';
            sendNotification?: boolean;
            sendSms?: boolean;
            secondaryPassword?: string;
        }
    ): Promise<void> => {
        return apiClient<void>(API_ENDPOINTS.USERS.RESEND_WELCOME(id), {
            method: 'POST',
            data: payload,
        });
    },

    /**
     * Bulk update user statuses.
     */
    bulkChangeStatus: async (userIds: string[], status: string): Promise<void> => {
        return apiClient<void>(API_ENDPOINTS.USERS.BULK.STATUS, {
            method: 'PATCH',
            data: { userIds, status },
        });
    },

    /**
     * Bulk reset user passwords.
     */
    bulkResetPassword: async (userIds: string[]): Promise<void> => {
        return apiClient<void>(API_ENDPOINTS.USERS.BULK.RESET_PASSWORD, {
            method: 'POST',
            data: { userIds },
        });
    },

    /**
     * Bulk delete users.
     */
    bulkDelete: async (userIds: string[]): Promise<void> => {
        return apiClient<void>(API_ENDPOINTS.USERS.BULK.DELETE, {
            method: 'POST',
            data: { userIds },
        });
    },

    /**
     * Export all data for the authenticated user.
     * @security Self-service privacy feature.
     */
    exportMyData: async (): Promise<Record<string, unknown>> => {
        return apiClient<Record<string, unknown>>(API_ENDPOINTS.USERS.ME.EXPORT, {
            method: 'GET',
        });
    },

    /**
     * Delete the authenticated user's own account.
     * @security Critical self-service operation.
     */
    deleteAccount: async (): Promise<void> => {
        return apiClient<void>(API_ENDPOINTS.USERS.ME.ACCOUNT, {
            method: 'DELETE',
        });
    },

    /**
     * Get the authenticated user's preferences (theme mode, UI settings).
     */
    getPreferences: async (): Promise<{ themeMode: 'light' | 'dark' | 'system'; uiSettings: Record<string, unknown> }> => {
        return apiClient<{ themeMode: 'light' | 'dark' | 'system'; uiSettings: Record<string, unknown> }>(API_ENDPOINTS.USERS.ME.PREFERENCES, {
            method: 'GET',
        });
    },

    /**
     * Update the authenticated user's preferences (theme mode, UI settings).
     */
    updatePreferences: async (payload: { themeMode?: 'light' | 'dark' | 'system'; uiSettings?: Record<string, unknown> }): Promise<{ themeMode: 'light' | 'dark' | 'system'; uiSettings: Record<string, unknown> }> => {
        const validatedPayload = updateUserPreferenceSchema.parse(payload);
        return apiClient<{ themeMode: 'light' | 'dark' | 'system'; uiSettings: Record<string, unknown> }>(API_ENDPOINTS.USERS.ME.PREFERENCES, {
            method: 'PUT',
            data: validatedPayload,
        });
    },

    /**
     * Fetch the real-time status of a bulk operation (e.g., progress tracking).
     * @security Admin-only operation.
     */
    getBulkOperationStatus: async (jobId: string): Promise<BulkOperationLog> => {
        return apiClient<BulkOperationLog>(API_ENDPOINTS.USERS.BULK_OPERATION_STATUS(jobId), {
            method: 'GET',
        });
    },

    /**
     * Initiate a bulk user creation job from a CSV payload.
     * @security Admin-only operation.
     */
    bulkCreateUsers: async (formData: FormData): Promise<{ jobId: string }> => {
        return apiClient<{ jobId: string }>(API_ENDPOINTS.USERS.BULK_CREATE, {
            method: 'POST',
            data: formData,
        });
    },
};
